import { useEffect, useState } from "react";
import useAuth from "../../../components/hooks/useAuth";
import { useTheme } from "../../../components/hooks/useTheme";
import { branchTimezone, getTheCheckAdditionalCharges, setTheAddedDiscountList } from "../../../utils/summaryCalculation";
import { Button, Divider, FormControlLabel, Menu, MenuItem, Radio, RadioGroup } from "@mui/material";
import { validateMin } from "../../../utils/validateMin";
import SendQuotes from "./SendQuotes2";
import colorConfigs from "../../../configs/colorConfigs";
import { addAdditionalChargeApi, deleteAdditionalChargeApi, updateDiscount, updateOrderApi } from "../../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";

type ExtrasProps = {
  goToNextPage: (page: number) => void;
  getDataFromExtras: (extraValue: any[]) => void;
  getDataFromUpdateOrder: (locationFeePercentage: number, taxPercentage: number, governmentTaxPercentage: number, discountList: any[]) => void;
  summaryData: any;
  refreshTheSummary: () => void;
  refreshTheAdjustment: () => void;
  extraValueData: any[];
  reservationAdditionalChargeDataIsLoaded: boolean;
  branchId: number;
  formattedCreatedAt: string;
  vehicleClassId: number;
  pickupDate: string;
  pickupTime: string;
  returnDate: string;
  returnTime: string;
  pickupLocation: number;
  returnLocation: number;
  discountOptions: any[]
  appliedDiscountList: any[];
  securityDepositAmount: number;
  isCompletedOrCancelled: boolean;
  reservationId: number;
  locationFeeAmount: number;
  taxAmount: number;
  governmentTaxAmount: number;
  setLocationFeeAmount: (value: number) => void;
  setTaxAmount: (value: number) => void;
  setGovernmentTaxAmount: (value: number) => void;
  checkExtraValue: any[];
  setPleaseWait: (value: boolean) => void;
};

export const Extras = ({
  goToNextPage,
  getDataFromExtras,
  getDataFromUpdateOrder,
  summaryData,
  refreshTheSummary,
  refreshTheAdjustment,
  extraValueData,
  reservationAdditionalChargeDataIsLoaded,
  branchId,
  formattedCreatedAt,
  vehicleClassId,
  pickupDate,
  pickupTime,
  returnDate,
  returnTime,
  pickupLocation,
  returnLocation,
  discountOptions,
  appliedDiscountList,
  securityDepositAmount,
  isCompletedOrCancelled,
  reservationId,
  locationFeeAmount,
  taxAmount,
  governmentTaxAmount,
  setLocationFeeAmount,
  setTaxAmount,
  setGovernmentTaxAmount,
  checkExtraValue,
  setPleaseWait
}: ExtrasProps) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const [extraValue, setExtraValue] = useState<any[]>([]);
  const [orgainzedExtraData, setOrgainzedExtraData] = useState<any[]>([]);

  //Discount
  const [couponCode, setCouponCode] = useState<number>(0);
  const [manualDiscount, setManualDiscount] = useState<string>("0");
  const [isManualDiscountInAPercentage, setIsManualDiscountInAPercentage] = useState<boolean>(false);

  //Tax 
  const [locationFeePercentage, setLocationFeePercentage] = useState<number>(0);
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const [governmentTaxPercentage, setGovernmentTaxPercentage] = useState<number>(0);

  //Quotes
  const [isCreatingPdf, setIsCreatingPdf] = useState<boolean>(true);
  const [sendEmailPopUp, setSendEmailPopUp] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isCompletedOrCancelled)
      setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const UpdateTimezone = branchTimezone(formattedCreatedAt);

  const updateTheCheckAdjustment = () => {
    const orgainzedUpdatedExtraData = extraValue
      ?.filter((item: any) => {
        const additionalCharges = item?.additionalCharges?.filter((value: any) => !value.hideFromReservations);
        return additionalCharges?.length > 0;
      })
      ?.map((item: any) => {
        const additionalCharges = item?.additionalCharges?.map((value: any) => {
          const amountOrPercentage = (value.chargeType === "Amount"
            ? value?.anountOrPercentage
            : (summaryData?.totalBasePriceBeforeDiscount *
              parseFloat(value?.anountOrPercentage)) /
            100);

          return {
            ...value,
            basePrice:
              value?.reservationAdditionalId > 0
                ? value?.basePrice
                : amountOrPercentage,
            amountOrPercentage: amountOrPercentage,
            isChecked: value?.mandatory ? true : value?.isChecked,
            reservationAdditionalId: value?.reservationAdditionalId || 0
          }
        });

        return {
          ...item,
          additionalCharges
        };
      });

    setOrgainzedExtraData(orgainzedUpdatedExtraData);
    getDataFromExtras(getTheCheckAdditionalCharges(orgainzedUpdatedExtraData));
  }

  useEffect(() => {
    if (reservationId <= 0 && extraValue?.length > 0) {
      updateTheCheckAdjustment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData?.totalBasePriceBeforeDiscount, extraValue]);

  useEffect(() => {
    if (reservationId > 0 && extraValue?.length > 0) {
      updateTheCheckAdjustment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraValue]);

  useEffect(() => {
    setCouponCode(appliedDiscountList[0]?.discountId || 0);
    setManualDiscount(appliedDiscountList[1]?.discountAmount || 0.00);
    setIsManualDiscountInAPercentage(appliedDiscountList[1]?.discountType !== "Amount");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDiscountList])

  useEffect(() => {
    if (reservationAdditionalChargeDataIsLoaded) {
      const extraData = extraValueData?.map((item: any) => {
        const additionalCharges = item?.additionalCharges?.map((value: any) => {
          return {
            ...value,
            quantity: value?.quantity ?? 1,
            basePrice: value?.basePrice ?? 0,
            isChecked: value?.isChecked ?? false,
            reservationAdditionalId: value?.reservationAdditionalId ?? 0
          }
        });

        return {
          ...item,
          additionalCharges
        };
      });

      setExtraValue(extraData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationAdditionalChargeDataIsLoaded]);

  //Extra
  const handleTheExtraSubmit = async (categoryId: number, additionalChargeId: number, updateValue: any) => {
    if (!isCompletedOrCancelled) {

      const updatedExtraData = await Promise.all(
        orgainzedExtraData.map(async (item: any) => {
          if (item?.id !== categoryId) {
            return item;
          }

          const additionalCharges = await Promise.all(
            item?.additionalCharges?.map(async (value: any) => {
              if (value?.id !== additionalChargeId) {
                return value;
              }

              if (updateValue?.isChecked) {
                if (reservationId > 0) {
                  addAdditionalCharge(value);
                }
              } else {
                if (reservationId > 0) {
                  deleteAdditionalCharge(value?.reservationAdditionalId);
                }
              }

              return updateValue;
            })
          );

          return {
            ...item,
            additionalCharges
          };
        })
      );

      setExtraValue(updatedExtraData);
      setOrgainzedExtraData(updatedExtraData);
      getDataFromExtras(getTheCheckAdditionalCharges(updatedExtraData));
    }
  }

  const addAdditionalCharge = async (value: any) => {
    if (!isCompletedOrCancelled) {
      setPleaseWait(true);

      addAdditionalChargeApi(
        reservationId,
        value?.additionalChargeBranchId,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
          refreshTheAdjustment();
          handleTenantError(null);
        })
        .catch(error => {
          setPleaseWait(false);
          handleTenantError(error);
        })
    }
  }

  const deleteAdditionalCharge = async (id: number) => {
    if (!isCompletedOrCancelled) {
      setPleaseWait(true);

      deleteAdditionalChargeApi(
        reservationId,
        id,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
          refreshTheAdjustment();
          handleTenantError(null);
        })
        .catch(error => {
          setPleaseWait(false);
          handleTenantError(error);
        })
    }
  }


  //Discount And Taxes
  const updateOrder = () => {
    if (!isCompletedOrCancelled) {
      setPleaseWait(true);

      if (reservationId > 0) {
        const updatedAppliedDiscountList = setTheAddedDiscountList(couponCode, manualDiscount, isManualDiscountInAPercentage, discountOptions, appliedDiscountList);

        const processDiscounts = (): Promise<{
          applyDiscounts: any[],
          updateDiscounts: any[],
          deleteDiscountIds: any[]
        }> => {
          return new Promise((resolve) => {
            const applyDiscounts: any[] = [];
            const updateDiscounts: any[] = [];
            const deleteDiscountIds: any[] = [];

            const newDiscountList = [...updatedAppliedDiscountList];

            appliedDiscountList?.forEach((item: any, index: number) => {
              if (index === 0) {
                if (item?.discountId === 0 && newDiscountList[0]?.discountId !== 0) {
                  applyDiscounts.push({
                    id: 0,
                    reservationId: reservationId,
                    discountId: newDiscountList[0]?.discountId,
                    discountDefinition: newDiscountList[0]?.discountDefinition,
                    discountType: newDiscountList[0]?.discountType,
                    discountAmount: newDiscountList[0]?.discountAmount,
                    manualDiscount: false,
                    supportingDocuments: false,
                    dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                    createdAt: new Date(UpdateTimezone).toLocaleString(),
                    createdBy: auth.givenname
                  });

                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: true
                  };
                } else if (
                  item?.discountId !== 0 &&
                  newDiscountList[0]?.discountId !== 0 &&
                  item?.discountId !== newDiscountList[0]?.discountId
                ) {
                  updateDiscounts.push({
                    id: newDiscountList[0]?.id,
                    reservationId: reservationId,
                    discountId: newDiscountList[0]?.discountId,
                    discountDefinition: newDiscountList[0]?.discountDefinition,
                    discountType: newDiscountList[0]?.discountType,
                    discountAmount: newDiscountList[0]?.discountAmount,
                    manualDiscount: false,
                    supportingDocuments: false,
                    dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                    createdAt: new Date(UpdateTimezone).toLocaleString(),
                    createdBy: auth.givenname
                  });

                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: true
                  };
                } else if (item?.discountId !== 0 && newDiscountList[0]?.discountId === 0) {
                  deleteDiscountIds.push(appliedDiscountList[0]?.id);

                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: false
                  };
                }
              }

              if (index === 1) {
                if (!item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                  applyDiscounts.push({
                    id: 0,
                    reservationId: reservationId,
                    discountId: newDiscountList[1]?.discountId,
                    discountDefinition: newDiscountList[1]?.discountDefinition,
                    discountType: newDiscountList[1]?.discountType,
                    discountAmount: newDiscountList[1]?.discountAmount,
                    manualDiscount: true,
                    supportingDocuments: false,
                    dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                    createdAt: new Date(UpdateTimezone).toLocaleString(),
                    createdBy: auth.givenname
                  });

                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: true
                  };
                } else if (item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                  updateDiscounts.push({
                    id: newDiscountList[1]?.id,
                    reservationId: reservationId,
                    discountId: 0,
                    discountDefinition: newDiscountList[1]?.discountDefinition,
                    discountType: newDiscountList[1]?.discountType,
                    discountAmount: newDiscountList[1]?.discountAmount,
                    manualDiscount: true,
                    supportingDocuments: false,
                    dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                    createdAt: new Date(UpdateTimezone).toLocaleString(),
                    createdBy: auth.givenname
                  });

                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: true
                  };
                } else if (item?.discountApplied && newDiscountList[1]?.discountAmount <= 0) {
                  deleteDiscountIds.push(appliedDiscountList[1]?.id);

                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: false
                  };
                }
              }
            });

            resolve({
              applyDiscounts,
              updateDiscounts,
              deleteDiscountIds
            });
          });
        };

        processDiscounts().then(({ applyDiscounts, updateDiscounts, deleteDiscountIds }) => {
          if (summaryData?.dateTimeCheckOut) {
            updateDiscount(
              {
                reservationDiscount: [...applyDiscounts, ...updateDiscounts],
                reservationDiscountIds: deleteDiscountIds
              },
              reservationId,
              auth?.id,
              tenantId
            )
              .then(() => {
                refreshTheSummary()
                handleTenantError(null);
              })
              .catch(error => {
                setPleaseWait(false)
                handleTenantError(error);
              });
          } else {
            updateOrderApi(
              {
                reservationDiscount: [...applyDiscounts, ...updateDiscounts],
                reservationDiscountIds: deleteDiscountIds,
                locationFeePercentage: locationFeePercentage,
                taxPercentage: taxPercentage,
                governmentTaxPercentage: governmentTaxPercentage
              },
              reservationId,
              auth?.id,
              tenantId
            )
              .then(() => {
                refreshTheSummary()
                handleTenantError(null);
              })
              .catch(error => {
                setPleaseWait(false)
                handleTenantError(error);
              });
          }
        });
      } else {
        const updatedAppliedDiscountList = setTheAddedDiscountList(couponCode, manualDiscount, isManualDiscountInAPercentage, discountOptions, appliedDiscountList);

        const processDiscounts = (): Promise<any[]> => {
          return new Promise((resolve) => {
            const newDiscountList = [...updatedAppliedDiscountList];

            appliedDiscountList?.forEach((item: any, index: number) => {
              if (index === 0) {
                if (item?.discountId === 0 && newDiscountList[0]?.discountId !== 0) {
                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: true
                  };
                } else if (
                  item?.discountId !== 0 &&
                  newDiscountList[0]?.discountId !== 0 &&
                  item?.discountId !== newDiscountList[0]?.discountId
                ) {
                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: true
                  };
                } else if (item?.discountId !== 0 && newDiscountList[0]?.discountId === 0) {
                  newDiscountList[0] = {
                    ...newDiscountList[0],
                    discountApplied: false
                  };
                }
              }

              if (index === 1) {
                if (!item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: true
                  };
                } else if (item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: true
                  };
                } else if (item?.discountApplied && newDiscountList[1]?.discountAmount <= 0) {
                  newDiscountList[1] = {
                    ...newDiscountList[1],
                    discountApplied: false
                  };
                }
              }
            });

            resolve(newDiscountList);
          });
        };

        processDiscounts().then((newList) => {
          getDataFromUpdateOrder(
            locationFeePercentage,
            taxPercentage,
            governmentTaxPercentage,
            newList
          );
        });
      }
    }
  }

  //Taxes
  useEffect(() => {
    setLocationFeePercentage(locationFeeAmount);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFeeAmount])

  useEffect(() => {
    setTaxPercentage(taxAmount);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxAmount])

  useEffect(() => {
    setGovernmentTaxPercentage(governmentTaxAmount);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governmentTaxAmount])


  return (
    <>
      <div className="w-full">
        <div className={`w-full pb-4`}>
          {
            orgainzedExtraData?.map((item: any, index: number) => {

              if (item?.additionalCharges?.every((value: any) => value?.mandatory)) {
                return <div key={index}></div>;
              }

              if (item?.additionalCharges?.every((value: any) => value.hideFromReservations)) {
                return <div key={index}></div>;
              }

              return (
                <div key={index} className="mb-2">
                  <div className="w-full mb-4">
                    <span className={`text-[16px] font-semibold ${themeStyles.main.textHeadingColor}`}>
                      {item?.categoryName}
                    </span>

                    {
                      item?.additionalCharges?.filter((value: any) => !value.hideFromReservations && !value.mandatory)
                        ?.map((value: any, i: number) => (
                          <div
                            key={i}
                            className={`flex justify-between items-center bg-transparent px-2 py-3 mt-1 w-full h-full min-h-max rounded-lg shadow-sm border ${themeStyles.main.borderColor}`}
                          >
                            <div className="flex gap-1.5 ">
                              <span className={`text-[14px] font-medium ${themeStyles.main.textHeadingColor}`}>
                                {value?.name}
                              </span>

                              {
                                value?.recommended && (
                                  <div className="flex justify-end">
                                    <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold bg-[#D9F4D4]  text-[#039855] border ${themeStyles.main.borderColor}`}>
                                      Recommended
                                    </span>
                                  </div>
                                )
                              }
                            </div>

                            <div className={`px-3 py-3 flex gap-1.5 items-center ${themeStyles.main.borderColor} border font-semibold rounded text-sm`}>
                              <input
                                type="checkbox"
                                className="cursor-pointer"
                                checked={value?.isChecked ? true : false}
                                onChange={() => {
                                  handleTheExtraSubmit(
                                    item?.id,
                                    value?.id,
                                    {
                                      ...value,
                                      isChecked: !value.isChecked
                                    }
                                  );
                                }}
                              />

                              <span className={`text-sm font-medium ${themeStyles.main.textHeadingColor}`}>
                                {value?.chargeType === "Amount" ? "$" : ""}{parseFloat(value?.anountOrPercentage)?.toFixed(2)}{value?.chargeType === "Amount" ? "" : "%"}
                                {value?.selectionType === "Only One" ? " One Time" : "/Day"}
                              </span>
                            </div>
                          </div>
                        ))
                    }
                  </div>
                  <Divider className={`w-full ${themeStyles.main.borderColor}`} />
                </div>
              )
            })
          }

          <form className="mt-4">
            <span className={`text-[14px] font-semibold ${themeStyles.main.textHeadingColor}`}>
              Discount
            </span>

            <div className="w-full px-2 my-2">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-0">
                  <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Coupon Code</label>
                  <select
                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(parseInt(e.target.value));
                    }}
                  >
                    <option value={0}>Select...</option>
                    {
                      discountOptions?.map((item: any) => {
                        return <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.name}
                        </option>;
                      })
                    }
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-[16px]">
                <div className="space-y-0">
                  <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Manual Discount({isManualDiscountInAPercentage ? "%" : "$"})</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                    value={manualDiscount}
                    defaultValue={0.00}
                    onChange={(e) => {
                      setManualDiscount(validateMin(e.target.value, 0).toString());
                    }}
                  />
                </div>

                <div className="space-y-0">
                  <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Manual Discount is a Percentage?</label>
                  <RadioGroup
                    row
                    value={isManualDiscountInAPercentage ? "Yes" : "No"}
                    onChange={(e) => setIsManualDiscountInAPercentage(e.target.value === "Yes" ? true : false)}
                  >
                    <FormControlLabel
                      value="Yes"
                      control=
                      {
                        <Radio
                          sx={{
                            color: "gray",
                            "&.Mui-checked": {
                              color: "#167AC3",
                            },
                          }}
                        />
                      }
                      label="Yes"
                      sx={{
                        color: themeStyles.form.labelTextColor
                      }}
                    />

                    <FormControlLabel
                      value="No"
                      control={
                        <Radio
                          sx={{
                            color: "gray",
                            "&.Mui-checked": {
                              color: "#167AC3",
                            },
                          }}
                        />
                      }
                      label="No"
                      sx={{
                        color: themeStyles.form.labelTextColor
                      }}
                    />

                  </RadioGroup>
                </div>
              </div>
            </div>

            {(!summaryData?.dateTimeCheckOut)
              && (
                <>
                  <span className={`text-[14px] font-semibold ${themeStyles.main.textHeadingColor}`}>
                    Taxes
                  </span>

                  <div className="w-full px-2 mt-2">
                    <div className="flex gap-1.5">
                      <div>
                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Location Tax %</label>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                          value={locationFeeAmount}
                          onChange={(e) => setLocationFeeAmount(validateMin(e.target.value, 0))}
                        />
                      </div>

                      <div>
                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Sales Tax %</label>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                          value={taxAmount}
                          onChange={(e) => setTaxAmount(validateMin(e.target.value, 0))}
                        />
                      </div>

                      <div>
                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Government Tax %</label>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                          value={governmentTaxAmount}
                          onChange={(e) => setGovernmentTaxAmount(validateMin(e.target.value, 0))}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

            <div className="flex gap-4 mt-6">
              <Button variant="contained"
                sx={{
                  backgroundColor: colorConfigs.btn.main.bg
                }}
                type="button"
                onClick={() => {
                  setTimeout(() => updateOrder(), 0);
                }}
              >
                Update Order
              </Button>

              <Button variant="contained"
                sx={{
                  backgroundColor: colorConfigs.btn.main.bg
                }}
                type="button"
                onClick={() => {
                  getDataFromExtras(getTheCheckAdditionalCharges(orgainzedExtraData));
                  goToNextPage(3);
                }}
              >
                Next Page
              </Button>
              {
                summaryData?.status === "Quotes" && (
                  <div>
                    <Button variant="contained"
                      sx={{
                        backgroundColor: colorConfigs.btn.main.bg
                      }}
                      type="button"
                      onClick={handleClick}
                    >
                      Quote {open ? <span className={`material-symbols-outlined text-white text-[25px] font-extralight`}>
                        keyboard_arrow_up
                      </span> : <span className={`material-symbols-outlined text-white text-[25px] font-extralight`}>
                        keyboard_arrow_down
                      </span>}
                    </Button>

                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          sx: {
                            width: 170,
                            backgroundColor: themeStyles.topBar.bg
                          }
                        }
                      }}
                    >
                      <MenuItem

                        sx={{ fontSize: "12px", marginBlock: "0px" }}
                        onClick={() => {
                          setSendEmailPopUp(true);
                          setIsCreatingPdf(true);
                          setAnchorEl(null);

                        }}>
                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>Create PDF</span>
                      </MenuItem>
                      <Divider className="w-full" />

                      <MenuItem
                        sx={{ fontSize: "12px", marginBlock: "0px" }}
                        onClick={() => {
                          setSendEmailPopUp(true);
                          setIsCreatingPdf(false);
                          setAnchorEl(null);

                        }}>
                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>Send Email</span>
                      </MenuItem>
                      <Divider className="w-full" />
                    </Menu>
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </div>

      {
        (summaryData?.status === "Quotes" && sendEmailPopUp)
        && (
          <SendQuotes
            sendEmailPopUp={sendEmailPopUp}
            hideSendEmailPopup={() => setSendEmailPopUp(false)}
            branchId={branchId}
            vehicleClassId={vehicleClassId}
            pickupDate={pickupDate}
            pickupTime={pickupTime}
            returnDate={returnDate}
            returnTime={returnTime}
            pickupLocation={pickupLocation}
            returnLocation={returnLocation}
            securityDepositAmount={securityDepositAmount}
            isCreatingPdf={isCreatingPdf}
            checkExtraValue={checkExtraValue}
            appliedDiscountList={appliedDiscountList}
            formattedCreatedAt={formattedCreatedAt}
            summaryData={summaryData}
            setPleaseWaitPopup={setPleaseWait}
          />
        )
      }
    </>
  )
}